import { Container } from "reactstrap";
import EmailRightSide from "./email-right-side/EmailRightSide";
import { useState } from "react";
import Breadcrumbs from "../../../common-elements/breadcrumbs/Breadcrumbs";
import { useTranslation } from "react-i18next";
import { EmailData } from "./types";

const LetterBoxContainer = () => {
  const { t } = useTranslation();
  const [composeEmail, setComposeEmail] = useState(false);
  const [emailValidation, setEmailValidation] = useState(false);
  const [searchWord, setSearchWord] = useState("");
  const [emailOpen, setEmailOpen] = useState<string[]>([]);
  const [emails, setEmails] = useState<EmailData[]>([
    {
      id: "11",
      recipients: ["Developers", "John Doe"],
      message: "Server Maintenance",
      subMessage: `Dear Developers and John Doe,

This email is to inform you of scheduled server maintenance that will take place tomorrow.

The maintenance window is scheduled from **1:00 AM to 3:00 AM IST**.

During this period, server availability may be intermittent. We recommend saving all your work before the maintenance window begins.

We apologize for any inconvenience this may cause and appreciate your understanding and cooperation.

Best regards,

The System Administrator`,
      time: "11:50 AM",
      color: "warning",
      badge: [{ title: "High", color: "danger" }],
    },
    {
      id: "12",
      recipients: ["Sales", "Team A"],
      message: "System Update",
      subMessage: "New API endpoints available today.",
      time: "12:00 PM",
      color: "success",
      badge: [{ title: "Medium", color: "info" }],
    },
    {
      id: "13",
      recipients: ["Team B", "Amal"],
      message: "Security Patch",
      subMessage: "Apply updates by 6:00 PM IST.",
      time: "12:10 PM",
      color: "danger",
      badge: [{ title: "High", color: "danger" }],
    },
    {
      id: "14",
      recipients: ["Developers", "Sales", "John"],
      message: "Database Optimization",
      subMessage: "Expect minor delays this evening.",
      time: "12:20 PM",
      color: "info",
      badge: [{ title: "Medium", color: "warning" }],
    },
    {
      id: "15",
      recipients: ["Team A", "Team B", "Amal", "John Doe"],
      message: "Platform Feedback Request",
      subMessage: "Share system issues via the portal.",
      time: "12:30 PM",
      color: "primary",
      badge: [{ title: "Low", color: "light" }],
    },
    {
      id: "16",
      recipients: ["Developers"],
      message: "API Rate Limit Increase",
      subMessage: "Effective from 8:00 AM IST tomorrow.",
      time: "12:40 PM",
      color: "success",
      badge: [{ title: "Medium", color: "info" }],
    },
    {
      id: "17",
      recipients: ["Sales", "Team A", "John"],
      message: "Service Upgrade",
      subMessage: "Improved performance starting next week.",
      time: "12:50 PM",
      color: "info",
      badge: [{ title: "Medium", color: "warning" }],
    },
    {
      id: "18",
      recipients: ["Team B", "Amal"],
      message: "System Downtime Alert",
      subMessage: "Brief outage at 11:00 PM IST tonight.",
      time: "1:00 PM",
      color: "dark",
      badge: [{ title: "High", color: "danger" }],
    },
    {
      id: "19",
      recipients: ["Developers", "Sales", "Team A"],
      message: "New Feature Rollout",
      subMessage: "Check the updated UI at 2:00 PM IST.",
      time: "1:10 PM",
      color: "primary",
      badge: [{ title: "High", color: "danger" }],
    },
    {
      id: "20",
      recipients: ["John Doe", "Amal", "Team B"],
      message: "Security Audit",
      subMessage: "Complete checks by end of day.",
      time: "1:20 PM",
      color: "warning",
      badge: [{ title: "High", color: "danger" }],
    },
    {
      id: "21",
      recipients: ["Developers", "John Doe"],
      message: "Bug Fix Deployment",
      subMessage: "Live at 3:00 PM IST today.",
      time: "1:30 PM",
      color: "success",
      badge: [{ title: "Medium", color: "info" }],
    },
    {
      id: "22",
      recipients: ["Sales", "Team A"],
      message: "Analytics Dashboard Update",
      subMessage: "New metrics available tomorrow.",
      time: "1:40 PM",
      color: "info",
      badge: [{ title: "Low", color: "light" }],
    },
    {
      id: "23",
      recipients: ["Team A", "Team B", "Amal", "John Doe"],
      message: "Load Balancer Upgrade",
      subMessage: "No downtime expected.",
      time: "1:50 PM",
      color: "secondary",
      badge: [{ title: "Medium", color: "warning" }],
    },
    {
      id: "24",
      recipients: ["Developers", "Sales", "John"],
      message: "Webhook Configuration Change",
      subMessage: "Review settings by 5:00 PM IST.",
      time: "2:00 PM",
      color: "danger",
      badge: [{ title: "High", color: "danger" }],
    },
    {
      id: "25",
      recipients: ["Developers"],
      message: "Deprecation Notice",
      subMessage: "Legacy API endpoints retire next month.",
      time: "2:10 PM",
      color: "warning",
      badge: [{ title: "Medium", color: "info" }],
    },
  ]);

  const handleAddEmail = (newEmail: {
    userEmail: string;
    subject: string;
    message: string;
  }) => {
    setEmails([
      ...emails,
      {
        id: `${emails.length + 1}`,
        recipients: newEmail.userEmail
          .split(",")
          .map((email) => email.trim().split("@")[0]),
        message: newEmail.subject,
        subMessage: newEmail.message.substring(0, 50),
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        color: "primary",
      },
    ]);
    setComposeEmail(false);
    setEmailValidation(false);
  };

  const toggleEmailOpen = (id: string) => {
    setEmailOpen((prev) =>
      prev.includes(id)
        ? prev.filter((emailId) => emailId !== id)
        : [...prev, id]
    );
  };

  return (
    <>
      <Breadcrumbs mainTitle={t("Email")} parent={t("Communication")} />
      <Container fluid>
        <EmailRightSide
          composeEmail={composeEmail}
          setComposeEmail={setComposeEmail}
          emailValidation={emailValidation}
          setEmailValidation={setEmailValidation}
          searchWord={searchWord}
          setSearchWord={setSearchWord}
          emails={emails}
          handleAddEmail={handleAddEmail}
          emailOpen={emailOpen}
          toggleEmailOpen={toggleEmailOpen}
        />
      </Container>
    </>
  );
};

export default LetterBoxContainer;
